<script setup lang="ts">
/**
 * Строка списка встреч (макеты 12-1, 12-7).
 *
 * Своим компонентом — потому что рисуется дважды: сама строка серии и её
 * экземпляры под раскрытием. Одна и та же строка, собранная в двух местах,
 * разошлась бы при первой же правке.
 */
import { NotebookPen, Repeat, Users, Video } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

import { useAuthStore } from '@/features/auth/store'

import { recurrenceLabel } from '../api'
import { formatNoteStamp } from '../notes'

import type { MeetingSession } from '../api'

defineProps<{
  meeting: MeetingSession
  isSelected: boolean
  /** Экземпляр под раскрытой серией: сдвинут и подписан скромнее */
  isNested?: boolean
  /** Строка серии: подписана правилом повтора, а не составом участников */
  isSeries?: boolean
}>()

const emit = defineEmits<{ select: [] }>()

const { t, locale } = useI18n()
const auth = useAuthStore()

/**
 * Состояние строки. Пилюлю рисуем только тогда, когда она что-то добавляет:
 * у обычной прошедшей встречи статус читается датой.
 */
const statusLabel = (meeting: MeetingSession): string => {
  switch (meeting.meetingStatus) {
    case 'CREATING':
      return t('meetings.status.creating')
    case 'CREATE_FAILED':
      return t('meetings.status.createFailed')
    case 'PROCESSING':
    case 'ENDED':
      return t('meetings.status.processing')
    case 'READY':
      return t('meetings.status.ready')
    case 'NO_RECORDING':
      return t('meetings.status.noRecording')
    case 'FAILED':
      return t('meetings.status.failed')
    case 'CANCELLED':
      return t('meetings.status.cancelled')
    default:
      return ''
  }
}

/** Тон пилюли: предупреждение и ошибка выделяются, остальное — нейтрально */
const statusTone = (meeting: MeetingSession): string => {
  switch (meeting.meetingStatus) {
    case 'READY':
      return 'ready'
    case 'NO_RECORDING':
      return 'warn'
    case 'CREATE_FAILED':
    case 'FAILED':
      return 'error'
    default:
      return 'muted'
  }
}

/** Когда встреча: у запланированной — её время, у прошедшей — когда была */
const when = (meeting: MeetingSession) =>
  meeting.scheduledStart
    ? new Date(meeting.scheduledStart).toLocaleString(locale.value, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : stamp(meeting)

const stamp = (meeting: MeetingSession) =>
  formatNoteStamp(meeting.createdAt, locale.value, {
    today: t('meetings.today'),
    yesterday: t('meetings.yesterday'),
  })

/**
 * Подпись строки серии: правило повтора и когда следующий созвон.
 *
 * ⚠ В колонку влезает около 34 знаков — дальше текст обрезается, и первым
 * уходит самое нужное. Поэтому «Каждый пн 10:00 · след. 22 сен», а счётчик
 * прошедших созвонов живёт отдельной строкой (макет 12-20).
 */
const seriesLabel = (meeting: MeetingSession) => {
  if (!meeting.seriesActive) {
    return meeting.meetingStatus === 'CANCELLED'
      ? t('meetings.series.cancelled')
      : t('meetings.series.ended')
  }

  // Только правило: когда ближайший созвон, справа уже написано датой, и
  // повторять это в подписи значит обрезать её многоточием — в колонку
  // влезает около 30 знаков.
  return recurrenceLabel(meeting.recurrence, meeting.scheduledStart, t)
}

/**
 * Подпись строки — кто был, без себя самого.
 *
 * «Ты, Анна, Иван» на каждой строке — это три четверти списка, занятые словом,
 * которое одинаково везде: человек и так знает, что был на своей встрече.
 */
const people = (meeting: MeetingSession) =>
  meeting.participants
    .filter((participant) => participant.joined && participant.userId !== auth.user?.id)
    .map((participant) => participant.name)
    .filter(Boolean)
    .join(', ')
</script>

<template>
  <button
    type="button"
    class="mlist__item"
    :class="{ 'mlist__item--selected': isSelected, 'mlist__item--nested': isNested }"
    @click="emit('select')"
  >
    <!-- Иконка говорит вид встречи: серия, созвон с Meet или заметки. -->
    <Repeat v-if="isSeries" :size="16" class="mlist__icon" aria-hidden="true" />
    <Video v-else-if="meeting.meetingId" :size="16" class="mlist__icon" aria-hidden="true" />
    <NotebookPen v-else-if="isNested" :size="16" class="mlist__icon" aria-hidden="true" />
    <Users v-else :size="16" class="mlist__icon" aria-hidden="true" />

    <span class="mlist__body">
      <span class="mlist__title">
        {{ meeting.title || t('meetings.item.title') }}
        <!-- Точка у идущей встречи: по ней её находят глазом, не читая. -->
        <span v-if="meeting.active" class="mlist__live" :title="t('meetings.item.live')" />
      </span>

      <span class="mlist__sub">
        {{ isSeries ? seriesLabel(meeting) : people(meeting) || t('meetings.item.alone') }}
      </span>

      <!-- Сколько созвонов серии уже прошло: в подпись это не влезает, а
           понять «встреча живёт давно» по одной дате нельзя. -->
      <span v-if="isSeries && meeting.seriesPastCount" class="mlist__sub">
        {{ t('meetings.series.pastCount', meeting.seriesPastCount) }}
      </span>

      <!-- У завершённой серии статус уже в подписи («Отменена» / «Завершена»),
           пилюля повторяла бы его слово в слово строкой ниже. -->
      <span
        v-if="statusLabel(meeting) && !(isSeries && !meeting.seriesActive)"
        class="mlist__status"
        :class="`mlist__status--${statusTone(meeting)}`"
      >
        {{ statusLabel(meeting) }}
      </span>
    </span>

    <span class="mlist__meta">
      <span class="mlist__stamp">{{ when(meeting) }}</span>
      <span v-if="meeting.noteCount" class="mlist__count">{{ meeting.noteCount }}</span>
    </span>
  </button>
</template>

<style scoped>
.mlist__item {
  display: flex;
  width: 100%;
  align-items: flex-start;
  gap: 10px;
  padding: 10px;
  border: none;
  border-radius: var(--radius-md);
  background: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.mlist__item:hover {
  background: var(--color-bg-muted);
}

.mlist__item--selected {
  background: var(--color-primary-tint);
}

/* Экземпляр серии сдвинут вправо: так видно, что он внутри строки выше, а не
   рядом с ней. */
.mlist__item--nested {
  padding-left: 26px;
}

.mlist__icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--color-text-muted);
}

.mlist__item--selected .mlist__icon {
  color: var(--color-primary);
}

.mlist__body {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.mlist__title {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
  line-height: 18px;
}

.mlist__live {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-success-strong);
}

.mlist__sub {
  overflow: hidden;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.mlist__meta {
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.mlist__stamp {
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  line-height: 14px;
}

.mlist__count {
  min-width: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  font-size: var(--font-size-overline);
  line-height: 16px;
  text-align: center;
}

.mlist__status {
  align-self: flex-start;
  margin-top: 2px;
  padding: 0 6px;
  border-radius: var(--radius-pill, 999px);
  font-size: var(--font-size-overline);
  line-height: 16px;
}

.mlist__status--muted {
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
}

.mlist__status--ready {
  background: var(--color-primary-tint);
  color: var(--color-primary);
}

.mlist__status--warn {
  background: var(--color-warning-tint, var(--color-bg-muted));
  color: var(--color-warning-strong, var(--color-text-secondary));
}

.mlist__status--error {
  background: var(--color-danger-tint, var(--color-bg-muted));
  color: var(--color-danger, var(--color-text-secondary));
}
</style>
